<template>
  <div class="container">
    <!-- File Selection Row -->
    <b-row class="mb-4">
      <b-col>
        <b-row align-v="center">
          <b-col md="2">
            <span>Select csv file</span>
          </b-col>
          <b-col md="10">
            <b-form-file v-model="file" accept=".csv" class="mt-2" plain :state="validateState('file-input')"
              aria-describedby="file-live-feedback" data-vv-name="file-input" data-vv-as="File"
              @change="onFileChange" />
            <b-form-input id="fileError-input" v-model="fileError" v-validate="{ is: '' }" hidden
              :state="validateState('fileError')" aria-describedby="fileError-live-feedback" data-vv-name="fileError"
              data-vv-as="File" />
            <b-form-invalid-feedback id="fileError-live-feedback">{{ fileError }}</b-form-invalid-feedback>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <!-- Tables and Operators Row -->
    <b-row class="mb-4">
      <!-- Tables Column -->
      <b-col md="6">
        <b-form-group>
          <template #label>
            <b-row align-h="between">
              <b-col cols="6">
                <label>
                  <i class="fas fa-dice-d6" /> Tables:
                  <span v-if="isLoadingTablesOptions"><i class="fas fa-spinner fa-pulse"></i></span>
                  <span v-else>({{ chosenTables.length }}/{{ tablesOptions.length }})</span>
                </label>
              </b-col>
            </b-row>
          </template>
          <div class="search-input-wrapper">
            <b-form-input
              v-model="tablesSearchQuery"
              placeholder="Search tables..."
              class="mb-2"
              :disabled="isLoadingTablesOptions"
            />
            <button
              v-if="tablesSearchQuery"
              class="clear-button"
              @click="clearTablesSearch"
              type="button"
            >
              ✕
            </button>
          </div>
          <div style="overflow-y: scroll; max-height: 300px;">
            <div class="custom-control custom-checkbox" v-for="(table, index) in filteredTablesOptions" :key="index">
              <input type="checkbox" class="custom-control-input" v-model="table.checked" :id="table.name + '-table'"
                :disabled="isLoadingTablesOptions" @change="onTablesChange">
              <label class="custom-control-label" :for="table.name + '-table'">
                <TableName :tableId="table.id" />
              </label>
            </div>
          </div>
          <div class="d-flex justify-content-end">
            <b-button @click="toggleAllTables" size="sm" class="toggle-link mt-2">Toggle All</b-button>
          </div>
          <b-form-input id="tablesError-input" v-model="tablesError" hidden v-validate="{ is: '' }"
            :state="validateState('tablesError')" aria-describedby="tablesError-live-feedback"
            data-vv-name="tablesError" data-vv-as="Tables" />
          <b-form-invalid-feedback id="tablesError-live-feedback">{{ tablesError }}</b-form-invalid-feedback>
        </b-form-group>
      </b-col>

      <!-- Operators Column -->
      <b-col md="6">
        <b-form-group>
          <template #label>
            <b-row align-h="between">
              <b-col cols="6">
                <label>
                  <RouteIcon operators /> Operators:
                  <span v-if="isLoadingOperatorsOptions"><i class="fas fa-spinner fa-pulse"></i></span>
                  <span v-else>({{ chosenOperators.length }}/{{ operatorsOptions.length }})</span>
                </label>
              </b-col>
            </b-row>
          </template>
          <div class="search-input-wrapper">
            <b-form-input
              v-model="operatorsSearchQuery"
              placeholder="Search operators..."
              class="mb-2"
              :disabled="isLoadingOperatorsOptions"
            />
            <button
              v-if="operatorsSearchQuery"
              class="clear-button"
              @click="clearOperatorsSearch"
              type="button"
            >
              ✕
            </button>
          </div>
          <div style="overflow-y: scroll; max-height: 300px;">
            <div class="custom-control custom-checkbox" v-for="(operator, index) in filteredOperatorsOptions" :key="index">
              <input type="checkbox" class="custom-control-input" v-model="operator.checked"
                :id="operator.name + '-operator'" :disabled="isLoadingOperatorsOptions" @change="onOperatorsChange">
              <label class="custom-control-label" :for="operator.name + '-operator'">
                {{ operator.name }}
              </label>
            </div>
          </div>
          <div class="d-flex justify-content-end">
            <b-button @click="toggleAllOperators" size="sm" class="toggle-link mt-2">Toggle All</b-button>
          </div>
          <b-form-input id="operatorsError-input" v-model="operatorsError" v-validate="{ is: '' }" hidden
            :state="validateState('operatorsError')" aria-describedby="operatorsError-live-feedback"
            data-vv-name="operatorsError" data-vv-as="Tables" />
          <b-form-invalid-feedback id="operatorsError-live-feedback">{{ operatorsError }}</b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Rewrite and Import Button Row -->
    <b-row class="mb-4">
      <b-col class="d-flex justify-content-between align-items-center">
        <b-form-checkbox v-model="rewrite" :value="1" :unchecked-value="0">
          <span class="font-weight-bold">Rewrite existing</span>
        </b-form-checkbox>
        <b-button @click="handleImport" variant="primary" :disabled="isLoading">Import</b-button>
      </b-col>
    </b-row>

    <!-- Results Row -->
    <b-row v-if="isLoaded">
      <b-col>
        <div class="results-container">
          <div class="results-header">
            <span class="font-weight-bold">Result:</span>
          </div>
          <div class="results-content">
            <div>Found in file: {{ this.limits.length }}</div>
            <div>New limits created: {{ this.created.length }}</div>
            <div>Updated: {{ this.updated.length }}</div>
            <div>Ignored: {{ this.ignored.length }}</div>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import TableName from "./TableName";
import RouteIcon from "../RouteIcon";

export default {

  data() {
    return {
      tablesOptions: [],
      tablesError: '',
      tablesSearchQuery: '',
      operatorsOptions: [],
      operatorsError: '',
      operatorsSearchQuery: '',
      isLoadingTablesOptions: false,
      isLoadingOperatorsOptions: false,
      isLoading: false,
      rewrite: '1',
      opened: true,
      file: null,
      fileError: '',
      isLoaded: false,
      limits: [],
      created: [],
      updated: [],
      ignored: [],
    }
  },
  components: {
    RouteIcon,
    TableName,
  },
  computed: {
    chosenTables() {
      return this.tablesOptions.filter(({ checked }) => checked);
    },
    chosenOperators() {
      return this.operatorsOptions.filter(({ checked }) => checked);
    },
    filteredTablesOptions() {
      if (!this.tablesSearchQuery) {
        return this.tablesOptions;
      }
      const searchLower = this.tablesSearchQuery.toLowerCase();
      return this.tablesOptions.filter(table => {
        const tableName = table.name ? table.name.toLowerCase() : '';
        const tableId = table.id ? table.id.toString().toLowerCase() : '';

        return tableName.includes(searchLower) || tableId.includes(searchLower);
      });
    },
    filteredOperatorsOptions() {
      if (!this.operatorsSearchQuery) {
        return this.operatorsOptions;
      }
      const searchLower = this.operatorsSearchQuery.toLowerCase();
      return this.operatorsOptions.filter(operator => {
        const operatorName = operator.name ? operator.name.toLowerCase() : '';
        return operatorName.includes(searchLower);
      });
    },
  },
  methods: {
    onOperatorsChange() {
      this.operatorsError = '';
    },
    onTablesChange() {
      this.tablesError = '';
    },
    onFileChange() {
      this.fileError = '';
    },
    validateState(ref) {
      if (
        this.veeFields[ref] &&
        (this.veeFields[ref].dirty || this.veeFields[ref].validated)
      ) {
        return !this.veeErrors.has(ref);
      }
      return null;
    },
    handleImport() {
      this.operatorsError = this.chosenOperators.length > 0 ? '' : 'Please select operators';
      this.tablesError = this.chosenTables.length > 0 ? '' : 'Please select tables';
      this.fileError = this.file === null ? 'Please select file' : '';

      this.isLoaded = false;

      this.$validator.validateAll().then(result => {
        if (!result) {
          return;
        }

        if (this.isLoading) {
          return;
        }

        this.isLoading = true;

        const fd = new FormData();
        fd.append('operators', this.chosenOperators.map(({ id }) => id).join(','));
        fd.append('tables', this.chosenTables.map(({ id }) => id).join(','));
        fd.append('file', this.file);
        fd.append('rewrite', this.rewrite);

        this
          .$http({
            url: '/api/v1/limits/import',
            method: 'POST',
            data: fd,
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then((res) => {
            const { limits, created, updated, ignored } = res.data;
            this.$emit('onImportComplete');
            this.$toastr.i('Success');
            this.isLoaded = true;
            this.limits = limits;
            this.created = created;
            this.updated = updated;
            this.ignored = ignored;

            this.resetForms();
          })
          .catch((err) => {
            let message = typeof err.response.data.message === 'string' ? err.response.data.message : JSON.stringify(err.response.data.message);
            this.$toastr.e(message);
          })
          .finally(() => {
            this.isLoading = false;
          })
      });
    },

    resetForms() {
      this.file = null;
      this.fileError = '';

      this.tablesOptions = this.tablesOptions.map(table => ({
        ...table,
        checked: false
      }));
      this.tablesError = '';
      this.tablesSearchQuery = '';

      this.operatorsOptions = this.operatorsOptions.map(operator => ({
        ...operator,
        checked: false
      }));
      this.operatorsError = '';
      this.operatorsSearchQuery = '';

      this.rewrite = '1';
    },
    onShow() { },
    onHidden() {
      this.$emit('close');
    },
    toggleAllTables() {
      this.tablesError = '';
      const visibleTables = this.filteredTablesOptions;
      const allVisibleChecked = visibleTables.length > 0 && visibleTables.every(({ checked }) => checked);

      this.tablesOptions = this.tablesOptions.map(table => {
        const isVisible = visibleTables.some(t => t.id === table.id);
        if (isVisible) {
          return { ...table, checked: !allVisibleChecked };
        }
        return table;
      });
    },
    toggleAllOperators() {
      this.operatorsError = '';
      const visibleOperators = this.filteredOperatorsOptions;
      const allVisibleChecked = visibleOperators.length > 0 && visibleOperators.every(({ checked }) => checked);

      this.operatorsOptions = this.operatorsOptions.map(operator => {
        const isVisible = visibleOperators.some(op => op.id === operator.id);
        if (isVisible) {
          return { ...operator, checked: !allVisibleChecked };
        }
        return operator;
      });
    },
    clearTablesSearch() {
      this.tablesSearchQuery = '';
    },
    clearOperatorsSearch() {
      this.operatorsSearchQuery = '';
    },
  },
  async beforeMount() {
    await this.$store.dispatch('tablesForSelect/loadList').then(() => {
      this.tablesOptions = this.$store.getters['tablesForSelect/list'].map(({ label, value }) => {
        return { id: value, name: label, checked: false }
      });
    });

    await this.$store.dispatch('operatorsForSelect/loadList').then(() => {
      this.operatorsOptions = this.$store.getters['operatorsForSelect/list'].map(({ label, value }) => {
        return { id: value, name: label, checked: false }
      });
    });
  },
}
</script>

<style scoped>
.results-container {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 15px;
  margin-top: 10px;
}

.results-header {
  text-align: left;
  margin-bottom: 10px;
}

.results-content {
  text-align: left;
  padding-left: 15px;
}

.results-content div {
  margin: 5px 0;
}

.toggle-link {
  background: none;
  border: none;
  color: #007bff;
  padding: 0;
  text-decoration: underline;
  text-decoration-style: dotted;
  margin-right: 20px;
}

.toggle-link:hover {
  background: none;
  color: #0056b3;
  text-decoration: underline;
  text-decoration-style: dotted;
}

.toggle-link:focus {
  box-shadow: none;
}

.toggle-link:active {
  background: none !important;
  color: #0056b3 !important;
}

.search-input-wrapper {
  position: relative;
}

.clear-button {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 0 5px;
  font-size: 14px;
  z-index: 10;
}

.clear-button:hover {
  color: #666;
}

.clear-button:focus {
  outline: none;
}
</style>
